import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { withTranslation, Trans } from 'react-i18next';
import { toast } from 'react-toastify';
import { ButtonGroup, Text, InternalGrid } from '@paygreen/paygreen-ui';
import {
    ValidateButton,
    ModalForm,
    SimpleActionButton,
    Message,
    Loader,
} from '../../../../components';
import { patchAssociation } from '../../service';

const CharityStripeValidateModal = ({
    t,
    setOpen,
    isOpen,
    accountData,
    refresh,
}) => {
    const [errorMessage, setErrorMessage] = useState(null);
    const [isLoading, setIsLoading] = useState();

    // we create a ref to detect if component is mounted
    const isMounted = useRef(false);

    const associationName = accountData?.association?.name;

    /**
     * PATCH request to API to validate charity's account
     */
    const validateAccount = () => {
        const validate = {
            enable: '1',
            validate: '1',
        };

        setIsLoading(true);

        patchAssociation(accountData?.association?.idAssociation, validate)
            .then(() => {
                toast(t('form.toastSuccess.validateCharity'));
                refresh();
            })
            .catch((error) => {
                const { detail, status } = error?.response?.data;

                setErrorMessage(status + ' / ' + detail);
            })
            .finally(() => {
                isMounted.current && setIsLoading(false);
            });
    };

    const cancelCloseModal = () => {
        setErrorMessage();
        setOpen(false);
    };

    useEffect(() => {
        isMounted.current = true;

        return () => {
            isMounted.current = false;
        };
    }, []);

    return (
        <ModalForm
            modalTitle={t('admin.charity.stripeAccount.validationTitle')}
            blockWidth="md"
            paddingLateral="md"
            isOpen={isOpen}
            actionOnClose={cancelCloseModal}
        >
            <Text textSize="sm">
                <Trans i18nKey="admin.charity.stripeAccount.validationMessage">
                    before validating
                    <Text textSize="sm" htmlTag="strong">
                        {{ associationName: associationName || '' }}
                    </Text>
                    verify
                </Trans>
            </Text>

            {errorMessage && (
                <InternalGrid justifyContent="center">
                    <Message
                        message={errorMessage}
                        type="error"
                        marginTop="md"
                        marginBottom="md"
                        paddingLateral="lg"
                    />
                </InternalGrid>
            )}

            <ButtonGroup marginTop="sm" marginBottom="xs">
                <a
                    href="https://dashboard.stripe.com/connect/accounts/overview"
                    target="_blank"
                    rel="noopener noreferrer"
                >
                    <SimpleActionButton
                        icon="ArrowRoundTopLeftIcon"
                        buttonSize="md"
                    >
                        {t('admin.charity.openStripe')}
                    </SimpleActionButton>
                </a>

                <ValidateButton
                    buttonSize="md"
                    onClick={validateAccount}
                    icon="CheckIcon"
                >
                    {t('admin.charity.validateAccount')}
                </ValidateButton>
            </ButtonGroup>

            {isLoading && <Loader isLoading={isLoading} />}
        </ModalForm>
    );
};

CharityStripeValidateModal.propTypes = {
    /**
     * @description setOpen and isOpen are automatically received from ModalControl parent
     */
    setOpen: PropTypes.func,
    isOpen: PropTypes.bool,
    accountData: PropTypes.object,
    setAccountData: PropTypes.func,
    refresh: PropTypes.func,
};

CharityStripeValidateModal.defaultProps = {
    isOpen: false,
};

export default withTranslation('common')(CharityStripeValidateModal);
